import React, { FC, ReactNode } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import IProduct from 'models/product';
import { APPLICATION_ROLES, SUPERUSER_APPROVAL } from 'components/constants-ts';
import { CheckEnergyError, isEnergyError } from './fields/nutrition';
import TagLabel from 'components/TagLabel';
import { HtmlTooltip } from 'components/inputfields/InputLabel';
import CheckRequireError, { checkRequireError } from './CheckRequireError';
import { isBlank } from 'utils/helpers-ts';
import { IImage } from 'components/images/ImageSorter';
import { scrollWithOffset } from './alerts';
import { IFoodlaCategoryOption } from 'models/category';
import { checkShortSalesDescriptionError } from './fields/short-sales-description';
import { useTheme } from '@material-ui/core';
import useViewport from 'hooks/useViewports';
import { useProductValidations } from './validation-context/ProductValidationContext';
import { ProductValidationError } from './validation-context/type';
import { getProductSaveValidation } from './validation-context/productSaveValidation';

export const checkEanErrorForIcaStoreProducer = (state?: IProduct, isCreatingNew?: boolean) => {
  const isProducerInICAGroup = state?.producerIsStore && state?.producerParentOrg === 'ica';
  let allowedEanPrefixes = ['2000', '2095', '2096', '2097'];
  if (isCreatingNew) {
    allowedEanPrefixes = ['2095'];
  }

  if (isProducerInICAGroup && !isBlank(state?.EAN)) {
    if (!allowedEanPrefixes.some(eanPrefix => !!state?.EAN?.trim().startsWith(eanPrefix))) {
      return `Det verkar som ni har angett en EAN-kod som inte tillhör det range som ICA Online har godkänt för butiksproducerade artiklar online. För att vi ska kunna släppa igenom produkten till ica.se, vänligen vårda om produkten för försäljning på en EAN-kod som inleds med följande siffror och ange samma EAN-kod på produkten i Sygrid: ${allowedEanPrefixes.map(eanPrefix => `“${eanPrefix}…”`).join(', ')}. Om artikeln kommer från en extern leverantör så hjälper vi till att få upp artikeln via leverantören. Gå till “My Products”-sidan i Sygrid och klicka på “BJUD IN LEVERANTÖR” uppe i högra hörnet.`;
    }
  }

  return '';
}

interface ErrorPanelWrapperProps {
  isSuperuser?: boolean;
  canSaveDraft?: boolean;
  children: ReactNode;
}

const ErrorPanelWrapper: FC<ErrorPanelWrapperProps> = ({ isSuperuser, canSaveDraft, children }) => {
  const theme = useTheme();

  const hasChildren = React.Children.toArray(children).length > 0;
  if (!hasChildren) {
    return null;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      borderRadius: 4,
      padding: 12,
      border: `1px solid ${theme.palette.divider}`,
      marginTop: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <InfoOutlinedIcon fontSize='small' color='primary' />
        <div style={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: 16 }}>
          {isSuperuser ? 'För att spara produkten med vald granskningsstatus' : 'För att skicka produkten för granskning'}
        </div>
      </div>
      {!!canSaveDraft && (
        <div style={{ paddingLeft: 27 }}>Du kan spara som utkast under tiden.</div>
      )}
      <div style={{ paddingLeft: 27 }}>
        {children}
      </div>
    </div>
  );
};

interface ISubmitButtonProps {
  isNoHasUnpaidInvoices?: boolean;
  state?: IProduct;
  savedProduct?: Partial<IProduct>;
  readOnly?: boolean;
  saving?: boolean;
  userRole?: string; // TODO enum:
  isCreatingNew?: boolean;
  approved?: boolean;
  disabled?: boolean;
  showEANError?: string;
  showNonUniqEANError?: string;
  isFood?: boolean;
  showCategoryError?: boolean;
  images?: IImage[];
  imagesToBeRemoved?: IImage[];
  rootCategory?: IFoodlaCategoryOption;
  imageProcessingStatus?: string;
  onSubmitDraft?: () => void;
}

// FC-1950: one label per missing retailer-required rule; clicking reveals and focuses the field.
const RequiredFieldErrorLabel = ({ error }: { error: ProductValidationError }) => {
  const expandTreeView = error.rule.expandTreeViews?.find(
    expand => !expand.forFields?.length || expand.forFields.includes(error.field)
  );
  return (
    <TagLabel focusable space type="error" onClick={() => scrollWithOffset(error.field, expandTreeView)}>
      {/* A rule message may end with a period; the label adds its own colon. */}
      <span>{error.message.replace(/\.$/, '')}:</span>
      <span style={{ marginLeft: 4, fontWeight: 400 }}>Klicka här</span>
    </TagLabel>
  );
};

export const SubmitButton = ({
  isNoHasUnpaidInvoices,
  state,
  savedProduct,
  readOnly,
  saving,
  userRole,
  isCreatingNew,
  approved,
  disabled,
  showEANError,
  showNonUniqEANError,
  isFood,
  showCategoryError,
  images,
  imagesToBeRemoved,
  rootCategory,
  imageProcessingStatus,
  onSubmitDraft,
}: ISubmitButtonProps) => {
  const { smUp } = useViewport();
  const isSuperuser = userRole === APPLICATION_ROLES.SUPERUSER;
  const imagesToSaveCount = (images?.length || 0) - (imagesToBeRemoved?.length || 0);
  
  const hasRequireError = checkRequireError(state, rootCategory);
  const hasEnergyError = isFood && isEnergyError(state);
  const noImageErrors = imagesToSaveCount > 0 || (isSuperuser && state?.adminStatus === SUPERUSER_APPROVAL.ADMIN_DISAPPROVED);
  const shortSalesDescriptionError = checkShortSalesDescriptionError(state?.shortSalesDescription);
  const required = useProductValidations();
  const { errors: requiredFieldErrors } = required;
  const saveValidation = getProductSaveValidation({
    state, savedProduct, isCreatingNew: !!isCreatingNew, rootCategory, userRole,
    imageCount: imagesToSaveCount, readOnly, processing: !!imageProcessingStatus, saving, disabled,
    eanError: showEANError, nonUniqueEanError: showNonUniqEANError, categoryError: showCategoryError,
    allReviewFieldsApproved: !!approved, isFood, required,
  });
  const hasTemperatureError = saveValidation.submissionErrors.some(({ field }) =>
    ['min_temperature', 'max_temperature', 'transportMinTemperature', 'transportMaxTemperature'].includes(field || '')
  );

  const getDisabled = () => {
    if (!saveValidation.canSubmit) return true;

    if (isSuperuser && state?.adminStatus === SUPERUSER_APPROVAL.ADMIN_DISAPPROVED) {
      return false;
    }

    // The existing native billing restriction is not part of the shared product-save policy.
    return userRole !== APPLICATION_ROLES.PRODUCER && !isNoHasUnpaidInvoices;
  };

  return (
    <div>
      <ErrorPanelWrapper
        isSuperuser={isSuperuser}
        canSaveDraft={!!onSubmitDraft && saveValidation.canSaveDraft}
      >
        {!!imageProcessingStatus && (
          <TagLabel focusable type="error" onClick={() => scrollWithOffset('images')} style={{ marginBottom: 8 }}>
            <span>Produkten kan inte sparas förrän bilderna är klara ({imageProcessingStatus}). Vänligen vänta:</span>
            <span style={{ marginLeft: 4, fontWeight: 400 }}>Klicka här</span>
          </TagLabel>
        )}
        {!noImageErrors && (
          <TagLabel focusable space type="error" onClick={() => scrollWithOffset('images')}>
            <span>Produktbild för e-handel är obligatoriskt:</span>
            <span style={{ marginLeft: 4, fontWeight: 400 }}>Klicka här</span>
          </TagLabel>
        )}
        {!!hasRequireError && <CheckRequireError state={state} rootCategory={rootCategory} />}
        {requiredFieldErrors.map(error => (
          <RequiredFieldErrorLabel key={error.rule.label} error={error} />
        ))}
        {!!shortSalesDescriptionError && (
          <TagLabel focusable space type="error" onClick={() => scrollWithOffset('shortSalesDescription')}>
            <span>{shortSalesDescriptionError}:</span>
            <span style={{ marginLeft: 4, fontWeight: 400 }}>Klicka här</span>
          </TagLabel>
        )}
        {!!(!isCreatingNew && showCategoryError && isSuperuser && state?.adminStatus === SUPERUSER_APPROVAL.ADMIN_APPROVED) && (
          <TagLabel space type='error'>Fill category field or select granular category</TagLabel>
        )}
        {!!isFood && !!state?.showRed && (
          <TagLabel space type='error'>Fill required nutritional fields</TagLabel>
        )}
        {!!isFood && hasTemperatureError && (
          <TagLabel space type='error'>Fill required temperature fields</TagLabel>
        )}
        {!!state?.showDimensionsError && (
          <TagLabel space type='error'>Fill required dimension fields</TagLabel>
        )}
        {!!showEANError && (
          <TagLabel space type='error'>{showEANError}</TagLabel>
        )}
        {!!showNonUniqEANError && (
          <TagLabel space type='error'>{showNonUniqEANError}</TagLabel>
        )}
        {!!(isSuperuser && state?.adminStatus === SUPERUSER_APPROVAL.ADMIN_APPROVED && !approved) && (
          <TagLabel space type='error'>All boxes needs to be reviewed and marked with a green check-mark before the product can be marked as Approved</TagLabel>
        )}
        {!!hasEnergyError && <CheckEnergyError state={state} />}
      </ErrorPanelWrapper>

      <div
        style={{
          display: 'flex',
          flexDirection: smUp ? 'row' : 'column',
          flexWrap: 'wrap',
          justifyContent: 'end',
          gap: 16,
          marginTop: 16,
        }}
      >
        {!!onSubmitDraft && (
          <HtmlTooltip
            arrow
            placement="top"
            title="'Spara som utkast' låter dig spara din produkt och komma tillbaka för att slutföra och publicera den senare, så att inga detaljer går förlorade innan vår granskning."
          >
            {/* The span keeps the tooltip working while the button is disabled. */}
            <span style={{ display: 'inline-flex', width: smUp ? undefined : '100%' }}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                fullWidth={!smUp}
                disabled={!saveValidation.canSaveDraft}
                onClick={onSubmitDraft}
              >
                SPARA SOM UTKAST
              </Button>
            </span>
          </HtmlTooltip>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth={!smUp}
          id={isCreatingNew ? 'create-product-button' : 'update-product-button'}
          disabled={getDisabled()}
        >
          {!!imageProcessingStatus ? (
            <>
              <CircularProgress size={20} color="inherit" style={{ marginRight: 10 }} />
              KAN INTE SPARA - {imageProcessingStatus.toUpperCase()} (TAR CA 1-3 MINUTER)
            </>
          ) : !isSuperuser ? (
            'Skicka för granskning'
          ) : isCreatingNew ? (
            'SKAPA PRODUKT'
          ) : (
            'UPPDATERA PRODUKT'
          )}
        </Button>
      </div>
    </div>
  );
};

export default SubmitButton;
